import { CounselingTypeEnum } from 'app/Bookings/constants'
import {
  FILTER_KEY_MAPPING,
  AvailabilityFilterValues,
  LabelAppointmentMapping,
} from 'app/ProviderSearch/constants'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'

/**
 * Description:
 * The preSelectAppointmentModeId and preSelectCounselingTypeId function is used to get the pre-selected filter values for the new booking flow
 * If the user hasn't selected any value from the previous screen, then this logic would look look at the provider if the provider supports only one value.
 * If the provider supports only one value, then that value would be pre-selected.
 * This functions should execute only once when the user lands on the booking flow.
 */

// Preselection logic of appointment mode when appointment mode is not selected from the previous screen as a filter
export const preSelectAppointmentModeId = (
  slot: ProviderSlot,
  groupedFilters: GroupedProviderFilter,
  existingAppointmentValue?: string | null
): string | null => {
  // Return null if slot or availabilityType is missing
  if (!slot || !slot.availabilityType || !slot.availabilityType.length) {
    return null
  }

  // Helper function to find filter ID by value
  const findFilterIdByValue = (value: string) => {
    const availabilityFilters =
      groupedFilters[FILTER_KEY_MAPPING.availabilityType] || []
    return (
      availabilityFilters.find((filter) => filter.value === value)?.id || null
    )
  }

  // Check if existingAppointmentValue is valid and slot supports it
  if (existingAppointmentValue) {
    const appointmentLabel = LabelAppointmentMapping[existingAppointmentValue]

    if (
      slot.availabilityType.some(
        (type) => type.toLowerCase() === appointmentLabel?.toLowerCase()
      )
    ) {
      return findFilterIdByValue(existingAppointmentValue)
    }
  }

  // If no valid existingAppointmentValue, check provider's available types
  for (const type of slot.availabilityType) {
    const typeValue = type.toLowerCase()

    if (typeValue === LabelAppointmentMapping['In Person']?.toLowerCase()) {
      return findFilterIdByValue(AvailabilityFilterValues.inPerson)
    } else if (typeValue === LabelAppointmentMapping['Online']?.toLowerCase()) {
      return findFilterIdByValue(AvailabilityFilterValues.online)
    }
  }

  return null
}

// Preselection logic of counseling type when counseling type is not selected from the previous screen as a filter
export const preSelectCounselingTypeId = (
  providerDetails: Provider
): string | null => {
  const counselingTypeSupportedByProvider = providerDetails.filters.filter(
    (filter) => filter.type === ProviderFilterTypes.COUNSELING_TYPE
  )

  if (counselingTypeSupportedByProvider.length === 1) {
    // If the provider supports only one counseling type, then preselect that value
    return counselingTypeSupportedByProvider[0].id
  }

  const individualCounselingByProvider = counselingTypeSupportedByProvider.find(
    (counselingType) => counselingType.value === CounselingTypeEnum.INDIVIDUAL
  )

  if (individualCounselingByProvider) {
    // If the provider supports individual counseling then preselect that value
    return individualCounselingByProvider.id
  }

  // If the provider doesn't support individual counseling then preselect the first counseling type
  return counselingTypeSupportedByProvider[0].id
}
